import { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Minus, Plus, Trash2, Tag, X, Check } from 'lucide-react';
import Breadcrumb from '../components/Breadcrumb';
import RecentlyViewed from '../components/RecentlyViewed';
import SEO from '../components/SEO';
import { Button } from '../components/ui/button';
import { useCart } from '../contexts/CartContext';
import { formatPrice } from '../lib/utils';

export default function Cart() {
  const { items, updateQty, remove, totals, coupon, applyCoupon, removeCoupon, knownCoupons } = useCart();
  const [couponInput, setCouponInput] = useState('');
  const [couponMsg, setCouponMsg] = useState(null);
  const [hint, setHint] = useState(false);

  const submitCoupon = (e) => {
    e.preventDefault();
    const res = applyCoupon(couponInput);
    setCouponMsg(res);
    if (res.ok) setCouponInput('');
    setTimeout(() => setCouponMsg(null), 3500);
  };

  return (
    <div data-testid="page-cart" className="container py-16 md:py-24">
      <SEO title="Your Bag" description="Review your Evermark selection." path="/cart" breadcrumb={[{ to: '/', label: 'Home' }, { label: 'Bag' }]} />
      <Breadcrumb items={[{ to: '/', label: 'Home' }, { label: 'Bag' }]} className="mb-6" />
      <p className="overline text-muted-foreground">The Bag</p>
      <h1 className="mt-3 font-display text-5xl md:text-7xl leading-[0.9]">Your Selection</h1>

      {items.length === 0 ? (
        <>
          <div className="py-24 md:py-32 text-center">
            <p className="font-display text-3xl mb-6">Nothing here — yet.</p>
            <Button asChild data-testid="cart-empty-cta"><Link to="/shop">Shop Everything</Link></Button>
          </div>
          <RecentlyViewed title="Perhaps one of these" eyebrow="Recently viewed" />
        </>
      ) : (
        <>
          {/* Free-ship progress meter */}
          {!totals.qualifiesFreeShip && (
            <div className="mt-10 border border-border p-6 md:p-8">
              <div className="flex items-baseline justify-between overline text-muted-foreground">
                <span>Complimentary shipping progress</span>
                <span className="tabular">{formatPrice(totals.amountToFreeShip)} to go</span>
              </div>
              <div className="mt-4 h-[3px] bg-border w-full overflow-hidden">
                <motion.div
                  className="h-full bg-foreground"
                  initial={{ width: 0 }}
                  animate={{ width: `${Math.min(100, (totals.subtotal / totals.shippingThreshold) * 100)}%` }}
                  transition={{ duration: 0.8, ease: [0.85, 0, 0.15, 1] }}
                  data-testid="cart-freeship-progress"
                />
              </div>
            </div>
          )}
          {totals.qualifiesFreeShip && totals.subtotal > 0 && (
            <p className="mt-10 overline text-muted-foreground inline-flex items-center gap-2" data-testid="cart-freeship-earned">
              <Check className="h-3 w-3" /> You've earned complimentary shipping.
            </p>
          )}

          <div className="mt-8 grid gap-16 md:grid-cols-[1fr_360px]">
            <ul className="divide-y divide-border">
              {items.map((item) => (
                <li key={item.key} className="py-6 flex gap-6" data-testid={`cart-page-item-${item.slug}`}>
                  <Link to={`/product/${item.slug}`} className="w-28 md:w-36 aspect-[3/4] bg-muted overflow-hidden shrink-0">
                    <img src={item.image} alt={item.name} className="h-full w-full object-cover" />
                  </Link>
                  <div className="flex-1 flex flex-col">
                    <div className="flex items-start justify-between gap-4">
                      <div>
                        <Link to={`/product/${item.slug}`} className="text-lg link-underline">{item.name}</Link>
                        <p className="mt-1 overline text-muted-foreground">{item.color?.name} · Size {item.size}</p>
                      </div>
                      <div className="text-right whitespace-nowrap">
                        <p className="tabular">{formatPrice(item.price * item.quantity)}</p>
                        {item.originalPrice > item.price && (
                          <p className="overline text-muted-foreground line-through tabular">{formatPrice(item.originalPrice * item.quantity)}</p>
                        )}
                      </div>
                    </div>
                    <div className="mt-auto flex items-center justify-between pt-4">
                      <div className="inline-flex items-center border border-border">
                        <button aria-label="Decrease quantity" className="h-9 w-9 inline-flex items-center justify-center" onClick={() => updateQty(item.key, item.quantity - 1)} data-testid={`cart-page-qty-dec-${item.slug}`}><Minus className="h-3 w-3" /></button>
                        <span className="w-10 text-center text-sm tabular" data-testid={`cart-page-qty-${item.slug}`}>{item.quantity}</span>
                        <button aria-label="Increase quantity" className="h-9 w-9 inline-flex items-center justify-center" onClick={() => updateQty(item.key, item.quantity + 1)} data-testid={`cart-page-qty-inc-${item.slug}`}><Plus className="h-3 w-3" /></button>
                      </div>
                      <button className="text-muted-foreground hover:text-foreground transition-colors duration-500 inline-flex items-center gap-2 text-sm" onClick={() => remove(item.key)} data-testid={`cart-page-remove-${item.slug}`}>
                        <Trash2 className="h-4 w-4" /> Remove
                      </button>
                    </div>
                  </div>
                </li>
              ))}
            </ul>

            {/* Summary */}
            <aside className="md:sticky md:top-32 md:self-start border border-border p-8 h-fit" data-testid="cart-summary">
              <p className="overline text-muted-foreground">Order summary</p>

              <div className="mt-6 space-y-3 text-sm">
                <div className="flex justify-between"><span>Subtotal</span><span className="tabular" data-testid="cart-summary-subtotal">{formatPrice(totals.subtotal)}</span></div>
                {coupon && (
                  <div className="flex justify-between items-center" data-testid="cart-summary-discount">
                    <span className="inline-flex items-center gap-2">
                      <Tag className="h-3 w-3" /> {coupon.code}
                      <button onClick={removeCoupon} aria-label="Remove coupon" className="opacity-60 hover:opacity-100" data-testid="cart-coupon-remove"><X className="h-3 w-3" /></button>
                    </span>
                    <span className="tabular">− {formatPrice(totals.couponDiscount)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span>Shipping</span>
                  <span className="tabular" data-testid="cart-summary-shipping">
                    {totals.shipping === 0 ? 'Complimentary' : formatPrice(totals.shipping)}
                  </span>
                </div>
                <div className="flex justify-between text-muted-foreground">
                  <span>Tax (est.)</span>
                  <span className="tabular" data-testid="cart-summary-tax">{formatPrice(totals.tax)}</span>
                </div>
                <div className="brushline my-4" />
                <div className="flex justify-between font-display text-2xl">
                  <span>Total</span>
                  <span className="tabular" data-testid="cart-page-total">{formatPrice(totals.total)}</span>
                </div>
              </div>

              {/* Coupon */}
              <form onSubmit={submitCoupon} className="mt-8" data-testid="cart-coupon-form">
                <label htmlFor="coupon" className="overline text-muted-foreground">Have a code?</label>
                <div className="mt-2 flex items-end gap-3 border-b border-foreground pb-2">
                  <input
                    id="coupon"
                    data-testid="cart-coupon-input"
                    value={couponInput}
                    onChange={(e) => setCouponInput(e.target.value.toUpperCase())}
                    placeholder="EVERMARK10"
                    className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground/50 tracking-widest text-sm py-1"
                  />
                  <button type="submit" data-testid="cart-coupon-apply" className="overline link-underline">Apply</button>
                </div>
                <AnimatePresence>
                  {couponMsg && (
                    <motion.p
                      initial={{ opacity: 0, y: 4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0 }}
                      className={`mt-2 overline ${couponMsg.ok ? 'text-foreground' : 'text-muted-foreground'}`}
                      data-testid={`cart-coupon-${couponMsg.ok ? 'success' : 'error'}`}
                    >
                      — {couponMsg.message}
                    </motion.p>
                  )}
                </AnimatePresence>
                <button type="button" onClick={() => setHint((h) => !h)} className="mt-3 overline text-muted-foreground link-underline" data-testid="cart-coupon-hint-toggle">
                  {hint ? 'Hide sample codes' : 'Try a sample code'}
                </button>
                <AnimatePresence>
                  {hint && (
                    <motion.ul
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="mt-2 space-y-1 overflow-hidden"
                    >
                      {knownCoupons.map((c) => (
                        <li key={c.code} className="overline text-muted-foreground">
                          <button
                            onClick={() => { setCouponInput(c.code); }}
                            data-testid={`cart-coupon-sample-${c.code}`}
                            className="link-underline"
                          >
                            {c.code}
                          </button>{' '}
                          — {c.label}
                        </li>
                      ))}
                    </motion.ul>
                  )}
                </AnimatePresence>
              </form>

              <Button
                className="w-full mt-8"
                size="lg"
                data-testid="cart-page-checkout"
                onClick={() => alert('Checkout will be enabled with Stripe when the backend goes live.')}
              >
                Proceed to Checkout
              </Button>
              <Button asChild variant="ghost" className="w-full mt-3"><Link to="/shop">Continue Shopping</Link></Button>

              <p className="mt-6 overline text-muted-foreground text-center">Secure checkout · Duties included at checkout</p>
            </aside>
          </div>

          <RecentlyViewed title="Perhaps also considered" eyebrow="Recently viewed" />
        </>
      )}
    </div>
  );
}
